/**
 * hb-pack-components.js — completa la llista de peces d'un pack.
 *
 * PER QUE EXISTEIX AQUEST FITXER
 * Liquid NO exposa els components d'un bundle natiu de Shopify. Verificat
 * contra la botiga: `variant.bundled_variants`, `variant.requires_components` i
 * `variant.component_parents` retornen null, i el volcat sencer de l'objecte
 * `product` no en porta cap rastre. Tampoc hi ha metafields. Es un gap conegut
 * (peticio oberta a la community des del nov. 2023, sense resposta); ni Dawn hi
 * dona suport. La via oficial de Shopify es una theme app block.
 *
 * `snippets/pack-components.liquid` se'n surt a mitges deduint les peces del
 * NOM de les opcions, pero nomes veu les que fan triar alguna cosa: una peca de
 * talla unica no genera opcio a Bundles i queda invisible.
 *
 * QUE FA
 * Demana els components a la STOREFRONT API, que si els exposa
 * (`ProductVariant.components`), i afegeix les files que falten.
 *
 * Verificat 18/08/2026 amb el pack `gorro-cuello` (2 peces, cap amb talla):
 * retorna les 2, amb imatge i URL. Cost de la query 83 sobre el limit de 1000.
 *
 * DETALLS QUE IMPORTEN
 * - Sense token: la Storefront API admet acces "tokenless" documentat. Es fa
 *   servir URL RELATIVA, aixi que la crida es same-origin i no hi ha CORS.
 * - Nomes veu productes PUBLICATS. Amb DRAFT retorna null i no passa res: la
 *   llista es queda amb el que hagi pintat Liquid.
 * - Degradacio: si la crida falla, no toca res del que ja hi ha.
 */
(function () {
  'use strict';

  var API_VERSION = '2025-10';

  // @inContext(language:) fa que l'API retorni TOT traduit — titols de
  // producte, titols de variant ("Unica"/"Unique"/"Einzigartig") i
  // onlineStoreUrl amb el prefix d'idioma. Sense aixo sempre torna l'idioma
  // principal de la botiga (castella), encara que el visitant navegui en /en/.
  // Una sola crida, amb @inContext(language:) perque titols, "Unique" i
  // onlineStoreUrl vinguin traduits.
  //
  // ⚠️ @inContext nomes s'aplica a la QUERY sencera, no a camps solts: la
  // Storefront API rebutja `product(...) @inContext(...)` amb
  // "directiveCannotBeApplied".
  //
  // L'aparellament component <-> selector es fa comparant selectedOptions
  // entre variants del pack, NO per titol: el nom de l'opcio que genera
  // Bundles ("Haramaki Merino | Azul & Crudo (Talla)") NO es tradueix mai,
  // mentre que el titol del producte SI. En /en/ i /de/ la comparacio per
  // titol no casava i tots els selectors quedaven amagats -> el pack no es
  // podia comprar. Els ids i els valors d'opcio, en canvi, son universals.
  var QUERY = [
    'query PackComponents($handle: String!, $language: LanguageCode!)',
    '  @inContext(language: $language) {',
    '  product(handle: $handle) {',
    '    variants(first: 100) { nodes {',
    '      id',
    '      requiresComponents',
    '      selectedOptions { name value }',
    '      components(first: 20) { nodes {',
    '        quantity',
    '        productVariant {',
    '          id',
    '          title',
    '          selectedOptions { name value }',
    '          product { id title handle onlineStoreUrl featuredImage { url } }',
    '        }',
    '      } }',
    '    } }',
    '  }',
    '}'
  ].join('\n');

  function imageUrl(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /**
   * URL de la peca. `onlineStoreUrl` ja ve amb el prefix d'idioma correcte
   * (/en/products/..., /de/products/...). El fallback l'ha de respectar
   * tambe: construir '/products/' a seques enviaria un visitant angles a la
   * fitxa en castella. `theme.routes.root_url` el publica el propi tema
   * (layout/theme.liquid:108) i val '/' o '/en', sense barra final.
   */
  function productUrl(product, handle) {
    if (product && product.onlineStoreUrl) return product.onlineStoreUrl;
    var root = (window.theme && window.theme.routes && window.theme.routes.root_url) || '';
    if (root === '/') root = '';
    return root + '/products/' + handle;
  }

  function buildRow(component, handle) {
    var variant = component.productVariant || {};
    var product = variant.product || {};
    var src = product.featuredImage ? product.featuredImage.url : '';

    var li = document.createElement('li');
    li.className = 'pack-components__item';
    li.setAttribute('data-pack-handle', handle);
    li.setAttribute('data-pack-from-api', '');

    var media = document.createElement('div');
    media.className = 'pack-components__media';
    if (src) {
      var a = document.createElement('a');
      a.href = productUrl(product, handle);
      a.setAttribute('tabindex', '-1');
      a.setAttribute('aria-hidden', 'true');
      var img = document.createElement('img');
      img.src = imageUrl(src, 168);
      img.srcset = imageUrl(src, 56) + ' 56w, ' + imageUrl(src, 112) + ' 112w, ' + imageUrl(src, 168) + ' 168w';
      img.sizes = '56px';
      img.loading = 'lazy';
      img.alt = '';
      img.width = 56;
      img.height = 56;
      a.appendChild(img);
      media.appendChild(a);
    } else {
      var ph = document.createElement('span');
      ph.className = 'pack-components__media-placeholder';
      ph.setAttribute('aria-hidden', 'true');
      media.appendChild(ph);
    }

    var body = document.createElement('div');
    body.className = 'pack-components__body';

    var title = document.createElement('p');
    title.className = 'pack-components__title';
    var link = document.createElement('a');
    link.href = productUrl(product, handle);
    link.textContent = product.title || '';
    title.appendChild(link);
    body.appendChild(title);

    // Peca sense eleccio: no hi ha selector, nomes la nota de talla unica.
    var note = document.createElement('p');
    note.className = 'pack-components__note';
    note.textContent = variant.title && variant.title !== 'Default Title' ? variant.title : '';
    if (note.textContent) body.appendChild(note);

    li.appendChild(media);
    li.appendChild(body);
    return li;
  }

  /**
   * Aparella cada component del pack amb l'opcio que el controla, SENSE
   * dependre de cap text.
   *
   * Com: es comparen dues variants del pack. Si en canviar el valor de
   * l'opcio X la variant del component C tambe canvia, aleshores X controla C.
   * Els ids i els valors d'opcio son universals; els titols no.
   *
   * Per que no per titol: Bundles genera el nom de l'opcio copiant el titol
   * del producte en l'idioma principal ("Haramaki Merino | Azul & Crudo
   * (Talla)") i NO el tradueix. El titol del producte, en canvi, si que es
   * tradueix. En /en/ i /de/ la comparacio no casava i tots els selectors
   * quedaven amagats: el pack no es podia comprar.
   *
   * Els noms d'opcio SON traduibles a l'admin, pero cada pack nou en crea de
   * nous sense traduir. Aparellant per valors, oblidar una traduccio nomes fa
   * que surti un text en castella, no que la fitxa deixi de funcionar.
   *
   * @returns {Object} productId (numeric) -> nom d'opcio del pack
   */
  function mapComponentsToOptions(variants) {
    var map = {};
    if (variants.length < 2) return map;

    var base = variants[0];
    var baseComponents = componentsByProduct(base);

    for (var i = 1; i < variants.length; i++) {
      var other = variants[i];
      var changedOptions = base.selectedOptions.filter(function (so, idx) {
        return other.selectedOptions[idx] && other.selectedOptions[idx].value !== so.value;
      });
      // Nomes serveixen les parelles que difereixen en UNA sola opcio.
      if (changedOptions.length !== 1) continue;

      var optionName = changedOptions[0].name;
      var otherComponents = componentsByProduct(other);

      Object.keys(baseComponents).forEach(function (productId) {
        if (map[productId]) return;
        var a = baseComponents[productId];
        var b = otherComponents[productId];
        if (b && a.variantId !== b.variantId) map[productId] = optionName;
      });
    }
    return map;
  }

  function componentsByProduct(variant) {
    var out = {};
    ((variant.components || {}).nodes || []).forEach(function (c) {
      var pv = c.productVariant || {};
      var pr = pv.product || {};
      if (pr.id) out[pr.id] = { variantId: pv.id, variant: pv };
    });
    return out;
  }

  function findFieldsetByOptionName(root, optionName) {
    if (!optionName) return null;
    var fieldsets = root.querySelectorAll('fieldset.option-selector[data-option]');
    for (var i = 0; i < fieldsets.length; i++) {
      if (fieldsets[i].getAttribute('data-option') === optionName) return fieldsets[i];
    }
    return null;
  }

  /**
   * Etiqueta del selector: el nom de l'opcio del PRODUCTE component
   * ("Talla"/"Size"/"Größe"), que ja ve traduit de l'admin com a qualsevol
   * altre producte. El nom d'opcio del pack no serveix: es autogenerat i porta
   * el titol del producte repetit.
   */
  function componentOptionLabel(componentVariant) {
    var opts = (componentVariant && componentVariant.selectedOptions) || [];
    return opts.length ? opts[0].name : '';
  }

  function render(root, components, optionByProduct) {
    var list = root.querySelector('[data-pack-list]');
    if (!list) return;

    // Els <fieldset> els ha pintat Liquid dins el <variant-picker>. Es
    // reaprofiten movent-los a la fila del seu component: NO se'n crea cap de
    // nou, perque el JS del tema (theme.js) ja els te cachejats i un duplicat
    // trencaria la seleccio.
    var orphanHost = root.querySelector('[data-orphan-options]');

    list.innerHTML = '';

    components.forEach(function (component) {
      var variant = component.productVariant || {};
      var product = variant.product || {};
      if (!product.handle) return;

      var li = buildRow(component, product.handle);
      var optionName = optionByProduct[product.id];
      var fieldset = optionName ? findFieldsetByOptionName(root, optionName) : null;

      if (fieldset) {
        // Peca amb eleccio: el selector va dins la fila, i la nota sobra.
        var note = li.querySelector('.pack-components__note');
        if (note) note.parentNode.removeChild(note);

        // L'etiqueta ha de ser la del PRODUCTE ("Size"/"Größe"), no el nom
        // autogenerat del pack, que no es tradueix.
        var label = componentOptionLabel(variant);
        var legend = fieldset.querySelector('legend');
        if (legend && label) legend.textContent = label;

        li.querySelector('.pack-components__body').appendChild(fieldset);
        li.setAttribute('data-pack-has-picker', '');
      }

      list.appendChild(li);
    });

    // Qualsevol selector que no hagi trobat fila es queda a la pagina (amagat
    // pero funcional): millor aixo que perdre'l i deixar el pack sense poder
    // seleccionar variant.
    if (orphanHost) {
      root.querySelectorAll('fieldset.option-selector[data-option]').forEach(function (fs) {
        if (!fs.closest('.pack-components__item')) orphanHost.appendChild(fs);
      });
    }

    root.setAttribute('data-pack-loaded', '');
  }

  function load(root) {
    var handle = root.getAttribute('data-product-handle');
    if (!handle) return;
    var version = root.getAttribute('data-api-version') || API_VERSION;
    var language = (root.getAttribute('data-language') || 'ES').toUpperCase();

    fetch('/api/' + version + '/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: QUERY,
        variables: { handle: handle, language: language }
      })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (payload) {
        var product = payload && payload.data && payload.data.product;
        if (!product) return;

        var bundled = (product.variants.nodes || [])
          .filter(function (v) { return v.requiresComponents; });
        if (!bundled.length) return;

        // La composicio es la mateixa a totes les variants del pack; el que
        // canvia es quina variant de cada peca hi entra.
        var components = (bundled[0].components || {}).nodes || [];
        if (!components.length) return;

        render(root, components, mapComponentsToOptions(bundled));
      })
      .catch(function () { /* degradacio silenciosa: es queda el render Liquid */ });
  }

  function init() {
    document.querySelectorAll('[data-pack-components]:not([data-pack-loaded])')
      .forEach(function (root) { load(root); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // El tema re-renderitza la seccio en canviar de variant: cal repassar-ho.
  document.addEventListener('on:variant:change', function () {
    window.setTimeout(init, 0);
  });
})();
